const CONFIG_SELECTOR = 'script[type="application/json"][data-motion-config]';
const THREE_CONFIG_SELECTOR = 'script[type="application/json"][data-motion-three-config]';

let runtimePromise;
let threeRuntimePromise;
const startedDocuments = new WeakSet();

function containsConfig(root) {
  return root?.matches?.(CONFIG_SELECTOR) || Boolean(root?.querySelector?.(CONFIG_SELECTOR));
}

function containsThreeConfig(root) {
  return root?.matches?.(THREE_CONFIG_SELECTOR) || Boolean(root?.querySelector?.(THREE_CONFIG_SELECTOR));
}

function loadStyles(documentRef, dataKey, linkDataKey) {
  if (documentRef.querySelector(`link[${linkDataKey}]`)) return;

  const bootstrap = documentRef.querySelector('script[data-motion-bootstrap]');
  const href = bootstrap?.dataset[dataKey];
  if (!href) return;

  const link = documentRef.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute(linkDataKey, '');
  documentRef.head.append(link);
}

async function getRuntime(documentRef) {
  loadStyles(documentRef, 'motionStylesheet', 'data-motion-stylesheet');
  runtimePromise ??= import('@theme/motion-runtime');
  return runtimePromise;
}

async function getThreeRuntime(documentRef) {
  loadStyles(documentRef, 'motionThreeStylesheet', 'data-motion-three-stylesheet');
  threeRuntimePromise ??= import('@theme/motion-three');
  return threeRuntimePromise;
}

async function scanIfConfigured(root, documentRef, options) {
  if (!runtimePromise && !containsConfig(root)) return;
  const runtime = await getRuntime(documentRef);
  runtime.scan(root, options);
}

async function scanThreeIfConfigured(root, documentRef) {
  if (!threeRuntimePromise && !containsThreeConfig(root)) return;
  const runtime = await getThreeRuntime(documentRef);
  runtime.scan(root);
}

async function scanConfigured(root, documentRef, motionOptions) {
  await Promise.all([
    scanIfConfigured(root, documentRef, motionOptions),
    scanThreeIfConfigured(root, documentRef),
  ]);
}

export function startMotionBootstrap(documentRef = globalThis.document) {
  if (!documentRef || startedDocuments.has(documentRef)) return;
  startedDocuments.add(documentRef);

  const scanDocument = () => scanConfigured(documentRef, documentRef);
  if (documentRef.readyState === 'loading') {
    documentRef.addEventListener('DOMContentLoaded', scanDocument, { once: true });
  } else {
    scanDocument();
  }

  documentRef.addEventListener('shopify:section:load', (event) => {
    scanConfigured(event.target, documentRef, { previewEntrances: true });
  });
  documentRef.addEventListener('shopify:section:unload', async (event) => {
    await Promise.all([
      runtimePromise ? runtimePromise.then((runtime) => runtime.dispose(event.target)) : undefined,
      threeRuntimePromise ? threeRuntimePromise.then((runtime) => runtime.dispose(event.target)) : undefined,
    ]);
  });
  documentRef.addEventListener('shopify:section:select', async (event) => {
    await Promise.all([
      runtimePromise ? runtimePromise.then((runtime) => runtime.update(event.target)) : undefined,
      threeRuntimePromise ? threeRuntimePromise.then((runtime) => runtime.update(event.target)) : undefined,
    ]);
  });
  documentRef.addEventListener('shopify:section:reorder', async (event) => {
    const root = event.target ?? documentRef;
    await Promise.all([
      runtimePromise ? runtimePromise.then((runtime) => runtime.update(root)) : undefined,
      threeRuntimePromise ? threeRuntimePromise.then((runtime) => runtime.update(root)) : undefined,
    ]);
  });
  globalThis.addEventListener?.('pagehide', async () => {
    await Promise.all([
      runtimePromise ? runtimePromise.then((runtime) => runtime.dispose(documentRef)) : undefined,
      threeRuntimePromise ? threeRuntimePromise.then((runtime) => runtime.dispose(documentRef)) : undefined,
    ]);
  }, { once: true });
}

if (typeof document !== 'undefined') startMotionBootstrap(document);
